(ns ui.tabs
  (:require
   ["@radix-ui/react-tabs" :refer [Content List Root Trigger]]
   ["lucide-react" :refer [ChevronLeft ChevronRight]]
   ["react-resize-detector" :refer [useResizeDetector]]
   [applied-science.js-interop :as jsi]
   [clojure.string :as string]
   [react :as react]
   [ui.utils :refer [clsx separate-props]]))

(defn- root-class [{:keys [class full-width]}]
  {:class        (clsx
                  {"w-full" full-width}
                  class)})

(defn tabs-root [& args]
  (let [[props children] (separate-props args)]
    (into [:> Root (merge
                    (dissoc props :full-width :variant :size)
                    (root-class props))]
          children)))

(defn trigger-class [{:keys [class variant size disabled]}]
  (let [variant (or variant :outlined)
        size    (or size :default)]
    (clsx "transition-colors relative flex justify-center items-center font-medium disabled:pointer-events-none disabled:cursor-default"
          (case variant
            :underlined (clsx "-mb-px text-kleene-dark-05 hover:bg-kleene-neutral-05 focus:bg-kleene-neutral-04 border-transparent hover:border-b-kleene-neutral-01 border-b-2 radix-state-active:border-kleene-secondary-01 radix-state-active:text-kleene-secondary-01"
                              "dark:text-kleene-neutral-01 dark:hover:bg-kleene-dark-02 dark:hover:border-b-kleene-neutral-03 dark:radix-state-active:border-kleene-secondary-v2 dark:radix-state-active:text-kleene-secondary-v2 dark:focus:bg-kleene-dark-03")
            :outlined   (str "text-kleene-dark-01 -ml-px first:ml-0 border border-kleene-neutral-02 -mb-px first:rounded-l last:rounded-r hover:bg-kleene-neutral-05 radix-state-active:bg-kleene-secondary-01 radix-state-active:border-kleene-secondary-01 radix-state-active:text-white "
                             "dark:border-kleene-dark-03 dark:radix-state-active:bg-kleene-secondary-v2 dark:hover:bg-kleene-dark-02 dark:text-kleene-neutral-01 dark:radix-state-active:text-white")
            :editor     (str "text-kleene-dark-01 -ml-px first:ml-0 border border-y-0 border-kleene-neutral-02 -mb-px hover:bg-kleene-neutral-05 radix-state-active:bg-kleene-secondary-01 radix-state-active:border-kleene-secondary-01 radix-state-active:text-white "
                             "dark:border-kleene-dark-03 dark:radix-state-active:bg-kleene-secondary-v2 dark:hover:bg-kleene-dark-02 dark:text-kleene-neutral-01 dark:radix-state-active:text-white ")
            (:tab :overlined) (clsx "bg-kleene-background-secondary hover:bg-kleene-background/50 border -ml-px radix-state-active:border-b-transparent radix-state-active:bg-kleene-background")
            nil)
          {"pointer-events-none opacity-50" disabled
           "radix-state-active:inset-shadow-kleene-secondary-01 radix-state-active:inset-shadow-[0_2px_0px] radix-state-active:border-t-0" (= variant :overlined)}
          (case size
            :default  "py-2 px-4"
            :small    "h-[35px] py-2 px-2 text-sm"
            :smallest "py-1 px-3 text-xs"
            :large    "py-3 px-6 text-lg"
            nil)
          class)))

(defn tabs-trigger [& args]
  (let [[props children] (separate-props args)]
    (into [:> Trigger (-> props
                          (dissoc :size :variant)
                          (assoc :class (trigger-class props)))]
          children)))

(defn tabs-list [& args]
  (let [[{:keys [ref class tabs size variant full-width on-scroll]} children] (separate-props args)
        underlined (= variant :underlined)
        editor     (= variant :editor)]
    (into [:> List {:ref   ref
                    :on-scroll on-scroll
                    :class (clsx "flex"
                                 {"overflow-y-hidden overscroll-none h-[35px]" editor
                                  "border-b border-kleene-neutral-02 dark:border-kleene-dark-02 leading-5" underlined
                                  "bg-kleene-background-secondary inset-shadow-kleene-layout-border inset-shadow-[0_0_0_1px]" (#{:tab :overlined} variant)
                                  "w-full *:flex-auto" full-width}
                                 (case size
                                   :default  "*:py-2 *:px-4"
                                   :small    "*:py-2 *:px-2 *:text-xs"
                                   :smallest "*:py-1 *:px-3 *:text-xs"
                                   :large    "*:py-3 *:px-6 *:text-lg"
                                   nil)
                                 class)}]
          (if tabs
            (for [{:keys [value label data-cy]} tabs]
              ^{:key value}
              [tabs-trigger {:value   value
                             :variant variant
                             :data-cy data-cy
                             :size    size}
               label])

            children))))

(defn tabs-content [& args]
  (let [[props children] (separate-props args)]
    (into [:> Content (dissoc props :variant)]
          children)))


(defn is-scrollable
  "Checks if element can be scrolled horizontally. Vertical tabs not supported yet."
  [element]
  (let [has-scrollable-content (> (jsi/get element :scrollWidth) (jsi/get element :clientWidth))
        overflow-x-style (jsi/get (jsi/call js/window :getComputedStyle element)
                                  :overflowX)
        is-overflow-hidden? (string/includes? overflow-x-style "hidden")]
    (and has-scrollable-content
         (not is-overflow-hidden?))))


(defonce scroll-position-cache (atom {}))


(defn tabs-fn [{:keys [tabs variant size full-width default-value disabled
                       activation-mode value on-value-change classes scrollable
                       id]}]

  (let [tabs-root-ref (react/useRef nil)
        tabs-list-ref (react/useRef nil)

        [scroll-position set-scroll-position] (react/useState (get @scroll-position-cache id 0))

        [can-be-scrolled? set-can-be-scrolled] (react/useState false)

        [can-scroll-left? set-can-scroll-left] (react/useState false)
        [can-scroll-right? set-can-scroll-right] (react/useState false)

        tabs-scroll-handler (fn tabs-scroll-handler-fn []
                              (let [list-element (jsi/get tabs-list-ref :current)
                                    left-scroll-position (jsi/get list-element :scrollLeft)
                                    max-scroll (- (jsi/get list-element :scrollWidth)
                                                  (jsi/get list-element :clientWidth))]

                                (set-scroll-position left-scroll-position)

                                (when id
                                  (swap! scroll-position-cache assoc id left-scroll-position))

                                (set-can-scroll-left
                                 (not= 0 left-scroll-position))

                                (set-can-scroll-right
                                 (< left-scroll-position max-scroll))))


        scroll-to-position (fn scroll-to-position-fn [position smooth]
                             (let [list-element (jsi/get tabs-list-ref :current)]
                               (jsi/call list-element :scrollTo #js {:left     position
                                                                     :behavior (if smooth "smooth" "instant")})))

        scroll-amount 150

        scroll-tabs-left (fn scroll-tabs-left-fn []
                           (scroll-to-position (- scroll-position scroll-amount) true))

        scroll-tabs-right (fn scroll-tabs-right-fn []
                            (scroll-to-position (+ scroll-position scroll-amount) true))]
    (when scrollable
      (react/useEffect
       (fn []
         (set-can-be-scrolled
          (is-scrollable (jsi/get tabs-list-ref :current)))

         (scroll-to-position scroll-position false)

         ;; return unmount callback
         (fn []))
       #js [tabs-root-ref tabs-list-ref]))


    (when scrollable
      (useResizeDetector #js {:handleWidth  true
                              :onResize     tabs-scroll-handler
                              :targetRef    tabs-root-ref}))

    [tabs-root {:ref tabs-root-ref
                :default-value   default-value
                :value           value
                :full-width      full-width
                :class           (:root classes)
                :activation-mode activation-mode
                :on-value-change on-value-change}

     [tabs-list {:ref             tabs-list-ref
                 :on-scroll       tabs-scroll-handler
                 :variant         variant
                 :size            size
                 :full-width      full-width
                 :class           (clsx (:list classes)
                                        {"relative px-5" (and scrollable can-be-scrolled?)})}
      (when (and scrollable
                 can-be-scrolled?
                 can-scroll-left?)
        [:div {:title "Scroll left"
               :on-click scroll-tabs-left
               :class ["sticky h-[35px] w-5 top-0 z-20"
                       "-left-5"
                       "flex items-center"
                       "text-kleene-dark-01 -ml-px first:ml-0 border border-y-0 border-kleene-neutral-02 -mb-px
                       bg-kleene-background  hover:bg-kleene-neutral-05 radix-state-active:bg-kleene-secondary-01 radix-state-active:border-kleene-secondary-01 radix-state-active:text-white"
                       "dark:border-kleene-dark-03 dark:radix-state-active:bg-kleene-secondary-v2 dark:hover:bg-kleene-dark-02 dark:text-kleene-neutral-01 dark:radix-state-active:text-white"
                       "cursor-pointer"]}
         [:> ChevronLeft]])



      (for [{:keys [value label class] :as tab-props} tabs]
        ^{:key value}
        [tabs-trigger (-> (dissoc tab-props :label :content)
                          (merge {:variant  variant
                                  :disabled disabled
                                  :size     size
                                  :class    [class (:trigger classes)]}))
         label])

      (when (and scrollable
                 can-be-scrolled?
                 can-scroll-right?)
        [:div {:title "Scroll right"
               :on-click scroll-tabs-right
               :class ["sticky h-[35px] w-5 top-0 z-20"
                       "-right-5"
                       "flex items-center"
                       "text-kleene-dark-01 -ml-px first:ml-0 border border-y-0 border-kleene-neutral-02 -mb-px  bg-kleene-background hover:bg-kleene-neutral-05 radix-state-active:bg-kleene-secondary-01 radix-state-active:border-kleene-secondary-01 radix-state-active:text-white"
                       "dark:border-kleene-dark-03 dark:radix-state-active:bg-kleene-secondary-v2 dark:hover:bg-kleene-dark-02 dark:text-kleene-neutral-01 dark:radix-state-active:text-white"
                       "cursor-pointer"]}
         [:> ChevronRight]])]

     (for [{:keys [value content]} tabs]
       ^{:key (str "value-" value)}
       [tabs-content {:value value
                      :class (:content classes)}
        content])]))

(defn tabs [params]
  [:f> tabs-fn params])
